/**
 * OnboardingModal – 3-stufiger Onboarding-Flow für neue User.
 * Schritt 1: Anzeigename
 * Schritt 2: Datenschutzeinstellungen
 * Schritt 3: Supporter werden (optional)
 */
import React, { useState } from "react";
import { User, Shield, ChevronRight, Check, Star, Zap, Heart, Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Toggle({ checked, onChange, label, description, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all text-left ${
        disabled
          ? "opacity-40 cursor-not-allowed border-zinc-800 bg-zinc-800/30"
          : checked
          ? "border-rose-500/30 bg-rose-500/8 cursor-pointer"
          : "border-zinc-800 bg-zinc-800/30 hover:border-zinc-700 cursor-pointer"
      }`}
    >
      <div className={`w-11 h-6 rounded-full flex-shrink-0 mt-0.5 transition-colors relative ${checked && !disabled ? "bg-rose-600" : "bg-zinc-700"}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? "left-6" : "left-1"}`} />
      </div>
      <div>
        <p className="text-sm font-bold text-white leading-tight">{label}</p>
        {description && <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </button>
  );
}

export default function OnboardingModal({ token, onComplete, publicUser }) {
  // Auth0-Username bereits vorhanden? → Step 1 überspringen
  const hasAuth0Username = publicUser?.username && !publicUser.username.includes("|");
  const [step, setStep] = useState(hasAuth0Username ? 2 : 1);
  const [username, setUsername] = useState(publicUser?.username || "");
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [widerrufAccepted, setWiderrufAccepted] = useState(false);
  const [profilPublic, setProfilPublic] = useState(false);
  const [inMemberList, setInMemberList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [error, setError] = useState("");
  const [savedUserData, setSavedUserData] = useState(null);
  const { t } = useTranslation();

  const headers = { Authorization: `Bearer ${token}` };

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true); setError("");
    try {
      await axios.put(`${BACKEND_URL}/api/me/me`, { username: username.trim() }, { headers });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  const handleStep2 = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/me/me`,
        { profil_public: profilPublic, in_member_list: profilPublic && inMemberList },
        { headers }
      );
      setSavedUserData(res.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan); setError("");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/subscriptions/checkout`,
        { plan, origin: window.location.origin },
        { headers }
      );
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || t("common.error"));
    } finally { setCheckoutLoading(null); }
  };

  const handleSkipSupporter = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/me/me`, { needs_onboarding_complete: true }, { headers });
    } catch {}
    onComplete(savedUserData || publicUser);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-0.5 bg-zinc-800">
          <div className="h-full bg-rose-600 transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} />
        </div>

        <div className="p-8">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition-all ${
                s < step ? "bg-rose-600 text-white" : s === step ? "bg-rose-600/20 text-rose-400 ring-1 ring-rose-600/40" : "bg-zinc-800 text-zinc-600"
              }`}>
                {s < step ? <Check size={12} /> : s}
              </div>
            ))}
            <span className="text-zinc-600 text-xs ml-1">{step}/3</span>
          </div>

          {/* ── STEP 1: NAME ── */}
          {step === 1 && (
            <>
              <div className="text-center mb-7">
                <div className="w-14 h-14 bg-rose-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User size={24} className="text-rose-400" />
                </div>
                <h2 className="text-2xl font-black text-white mb-2">{t("onboarding.welcome")}</h2>
                <p className="text-zinc-400 text-sm">{t("onboarding.step1_call")}</p>
              </div>

              <form onSubmit={handleStep1} className="space-y-4">
                {error && (
                  <div className="bg-red-900/20 border border-red-800/40 text-red-400 text-sm px-3 py-2 rounded-lg">
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1.5">
                    {t("onboarding.display_name")}
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder={t("onboarding.username_placeholder_example")}
                    autoFocus
                    required
                    maxLength={50}
                    data-testid="onboarding-username-input"
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !username.trim()}
                  data-testid="onboarding-submit-btn"
                  className="w-full py-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? t("onboarding.saving") : <><span>{t("onboarding.next_btn")}</span> <ChevronRight size={16} /></>}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full py-2 text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
                >
                  {t("onboarding.skip")}
                </button>
              </form>
            </>
          )}

          {/* ── STEP 2: DATENSCHUTZ ── */}
          {step === 2 && (
            <>
              <div className="text-center mb-7">
                <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield size={24} className="text-zinc-400" />
                </div>
                <h2 className="text-2xl font-black text-white mb-2">{t("onboarding.step2_title")}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed">{t("onboarding.step2_desc")}</p>
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800/40 text-red-400 text-sm px-3 py-2 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <div className="space-y-3 mb-6">
                <Toggle
                  checked={profilPublic}
                  onChange={setProfilPublic}
                  label={t("onboarding.public_profile_label")}
                  description={t("onboarding.public_profile_desc", { name: username || publicUser?.username || "" })}
                />
                <Toggle
                  checked={profilPublic && inMemberList}
                  onChange={setInMemberList}
                  disabled={!profilPublic}
                  label={t("onboarding.member_list_label")}
                  description={t("onboarding.member_list_desc") + " · " + t("onboarding.member_list_requires")}
                />
              </div>

              <button
                onClick={handleStep2}
                disabled={loading}
                data-testid="onboarding-privacy-submit-btn"
                className="w-full py-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {loading ? t("onboarding.saving") : <><span>{t("onboarding.save_privacy")}</span> <ChevronRight size={16} /></>}
              </button>
            </>
          )}

          {/* ── STEP 3: SUPPORTER ── */}
          {step === 3 && (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-rose-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star size={24} className="text-rose-400" />
                </div>
                <h2 className="text-2xl font-black text-white mb-1">{t("onboarding.step3_title")}</h2>
                <p className="text-zinc-400 text-sm">{t("onboarding.step3_subtitle")}</p>
              </div>

              <div className="space-y-2 mb-6">
                {[
                  { icon: Zap, key: "feature_hype" },
                  { icon: Heart, key: "feature_favorites" },
                  { icon: Bell, key: "feature_follows" },
                  { icon: Star, key: "feature_badge" },
                ].map(({ icon: Icon, key }) => (
                  <div key={key} className="flex items-center gap-3 text-sm text-zinc-300">
                    <Icon size={14} className="text-rose-400 flex-shrink-0" />
                    {t(`supporter_page.${key}`)}
                  </div>
                ))}
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800/40 text-red-400 text-sm px-3 py-2 rounded-lg mb-4">
                  {error}
                </div>
              )}

              <div className="space-y-2 mb-4">
                <label className="flex items-start gap-2.5 mb-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agbAccepted}
                    onChange={e => setAgbAccepted(e.target.checked)}
                    data-testid="onboarding-agb-checkbox"
                    className="mt-0.5 accent-rose-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-500 leading-relaxed group-hover:text-zinc-400 transition-colors">
                    {t("onboarding.agb_agree", { agb: () => "" })}
                    {" "}<a href="/page/agb" target="_blank" rel="noopener noreferrer" className="text-rose-400 hover:text-rose-300 underline underline-offset-2">AGB</a>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 mb-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={widerrufAccepted}
                    onChange={e => setWiderrufAccepted(e.target.checked)}
                    data-testid="onboarding-widerruf-checkbox"
                    className="mt-0.5 accent-rose-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-500 leading-relaxed group-hover:text-zinc-400 transition-colors">
                    {t("onboarding.widerruf_agree")}
                  </span>
                </label>
                <button
                  onClick={() => handleCheckout("monthly")}
                  disabled={!!checkoutLoading || !agbAccepted || !widerrufAccepted}
                  data-testid="onboarding-supporter-monthly-btn"
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold transition-colors"
                >
                  <span>{t("onboarding.start_monthly")}</span>
                  <span className="text-rose-200 font-normal text-sm">
                    {checkoutLoading === "monthly" ? t("onboarding.starting") : "3 €/Monat"}
                  </span>
                </button>
                <button
                  onClick={() => handleCheckout("yearly")}
                  disabled={!!checkoutLoading || !agbAccepted || !widerrufAccepted}
                  data-testid="onboarding-supporter-yearly-btn"
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white font-bold transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span>{t("onboarding.start_yearly")}</span>
                    <span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded-full font-bold">{t("supporter_page.plan_yearly_savings")}</span>
                  </div>
                  <span className="text-zinc-400 font-normal text-sm">
                    {checkoutLoading === "yearly" ? t("onboarding.starting") : "20 €/Jahr"}
                  </span>
                </button>
              </div>

              <button
                onClick={handleSkipSupporter}
                className="w-full py-2 text-zinc-600 hover:text-zinc-400 text-sm transition-colors"
                data-testid="onboarding-skip-supporter-btn"
              >
                {t("onboarding.maybe_later")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
